import { useEffect, useState } from 'react';
import { Button, Typography } from '@material-tailwind/react';
import { CalendarDaysIcon, PlayCircleIcon } from '@heroicons/react/24/outline';
import { checkToken } from '../../../api/utils/api/userApi';
import { getErrorMessages, logout } from '../../../api/utils/util-func';
import GuestFreeTrialModal from './GuestFreeTrialModal';
import { useNavigate } from 'react-router-dom';
import FreeTrialUnavailableModal from './FreeTrialUnavailableModal';
import { getFreeTrialQuota } from '../../../api/utils/free-trial/api';
import { openAlert } from '../../../store/features/modalSlice';
import { useAppDispatch } from '../../../store/hook';
import { AuthState } from '../../../constants/types/free-trial/type';
import FloatingBarFrame from './FloatingBarFrame';
import { useFreeTrialExternalLinks } from '../../../hooks/useFreeTrialExternalLink';

interface DefaultFreeTrialFloatingBarProps {
  isVisible: boolean;
  isScrolling: boolean;
}

const DefaultFreeTrialFloatingBar = ({
  isVisible,
  isScrolling,
}: DefaultFreeTrialFloatingBarProps) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const links = useFreeTrialExternalLinks();

  const [authState, setAuthState] = useState<AuthState>('checking');
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [isCheckingTrialCount, setIsCheckingTrialCount] = useState(false);
  const [isTrialUnavailableOpen, setIsTrialUnavailableOpen] = useState(false);

  /**
   * 로그인 상태 확인
   */
  useEffect(() => {
    const checkAuth = async () => {
      const res = await checkToken();

      if (res === 'EMPTY') {
        setAuthState('guest');
        return;
      }

      if (res === 'EXPIRED') {
        logout(false);
        setAuthState('guest');
        return;
      }

      const isStudent = localStorage.getItem('type') === 'STUDENT';

      setAuthState(isStudent ? 'student' : 'member');
    };

    void checkAuth();
  }, []);

  const handleClickApply = () => {
    window.open(links.FREE_TRIAL_SEMINAR_APPLY, '_blank');
  };

  const handleClickStart = async () => {
    if (authState === 'guest') {
      setIsGuestModalOpen(true);
      return;
    }

    if (authState !== 'member' || isCheckingTrialCount) {
      return;
    }

    try {
      setIsCheckingTrialCount(true);

      const data = await getFreeTrialQuota();

      if (data.remaining === 0) {
        setIsTrialUnavailableOpen(true);
        return;
      }

      navigate('/free-trial/apply');
    } catch (error) {
      console.error('무료체험 신청 가능 횟수 조회 실패', error);
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsCheckingTrialCount(false);
    }
  };

  if (authState === 'checking' || authState === 'student') {
    return null;
  }

  const floatingBarStateClass = isScrolling
    ? 'gap-1.5 rounded-[18px] p-1'
    : 'gap-2 rounded-[20px] p-2 lg:gap-4 lg:px-4 lg:py-2.5';

  const applyButtonStateClass = isScrolling
    ? 'h-10 flex-none gap-1.5 rounded-[14px] px-4 py-0'
    : 'h-[60px] flex-1 rounded-[16px] px-4 py-0 lg:h-14 lg:gap-2 lg:rounded-[14px] lg:px-6';

  const startButtonStateClass = isScrolling
    ? 'h-10 flex-none rounded-[14px] px-4 py-0'
    : 'h-[60px] flex-[2] gap-2 rounded-[16px] px-4 py-0 lg:h-14 lg:rounded-[14px] lg:px-6';

  const titleTextClass = isScrolling
    ? 'text-[13px]'
    : 'text-[16px] lg:text-[17px]';

  const freeBadgeStateClass = isScrolling
    ? 'max-w-0 px-0 py-0 opacity-0'
    : 'max-w-12 px-2 py-1 text-[10px] opacity-100 lg:text-xs';

  return (
    <>
      <FloatingBarFrame
        isVisible={isVisible}
        isScrolling={isScrolling}
        compactWidthClass="w-[167px]"
      >
        <div className="relative">
          {/* 상단 말풍선 */}
          <div
            className={`absolute -top-9 left-16 z-30 rounded-xl rounded-bl-none bg-[#211B2D] px-3.5 py-2 shadow-md transition-all duration-300 lg:-top-10 lg:left-14 ${
              isScrolling
                ? 'pointer-events-none translate-y-2 scale-90 opacity-0'
                : 'translate-y-0 scale-100 opacity-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full bg-[#FFD633]" />

              <Typography
                variant="small"
                className="whitespace-nowrap text-[11px] font-semibold leading-none text-white lg:text-[13px]"
              >
                엄마랑 10분! 첫 암송 해볼까요?
              </Typography>
            </div>
          </div>

          {/* 모바일 캐릭터 */}
          <div
            className={`absolute -top-12 left-1 z-10 flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border-2 border-white bg-[#FFF7D6] shadow-sm transition-all duration-300 lg:hidden ${
              isScrolling
                ? 'pointer-events-none translate-y-3 scale-75 opacity-0'
                : 'translate-y-0 scale-100 opacity-100'
            }`}
          >
            <img
              src="/images/free-trial/bready.png"
              alt="bready"
              className="h-full w-full object-contain"
            />
          </div>

          {/* Floating Bar */}
          <div
            className={`relative z-20 flex w-full items-stretch border border-[#E5BE00] bg-[#FFD928] shadow-[0_8px_24px_rgba(88,57,180,0.22)] transition-all duration-300 ease-out ${floatingBarStateClass} lg:w-max lg:items-center lg:rounded-full`}
          >
            {/* PC 캐릭터 */}
            {!isScrolling && (
              <div className="hidden h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#E6DFFF] bg-[#FFF7D6] lg:flex">
                <img
                  src="/images/free-trial/bready.png"
                  alt="bready"
                  className="h-full w-full object-contain"
                />
              </div>
            )}

            {/* PC 소개 문구 */}
            {!isScrolling && (
              <div className="hidden shrink-0 items-center whitespace-nowrap lg:flex">
                <Typography className="text-[15px] font-extrabold text-gray-900">
                  암송부터 <span className="text-[#6737F4]">AI 인증</span>
                  까지, 첫 레슨 무료
                </Typography>
              </div>
            )}

            {/* 설명회 신청 */}
            <Button
              ripple={false}
              onClick={handleClickApply}
              className={`flex min-w-0 items-center justify-center bg-[#F1EDFF] normal-case text-[#5B35DD] shadow-none transition-all duration-300 hover:shadow-none focus:shadow-none ${applyButtonStateClass} lg:flex-none lg:rounded-full`}
            >
              {/* compact mode 마스코트 */}
              {isScrolling && (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#D8CBFF] bg-[#FFF7D6]">
                  <img
                    src="/images/free-trial/bready.png"
                    alt=""
                    className="h-full w-full object-contain"
                  />
                </span>
              )}

              {/* PC 설명회 아이콘 */}
              {!isScrolling && (
                <CalendarDaysIcon className="hidden h-5 w-5 shrink-0 lg:block" />
              )}

              <div
                className={`flex items-center leading-none ${
                  isScrolling
                    ? 'flex-row'
                    : 'flex-col gap-1 lg:flex-row lg:gap-2'
                }`}
              >
                <span
                  className={`whitespace-nowrap font-bold transition-all duration-300 ${titleTextClass}`}
                >
                  {isScrolling ? '설명회' : '설명회 신청'}
                </span>
              </div>
            </Button>

            {/* 무료체험 */}
            <Button
              ripple={false}
              onClick={handleClickStart}
              disabled={isCheckingTrialCount}
              className={`group relative flex min-w-0 items-center justify-center overflow-visible bg-[#6737F4] normal-case text-white shadow-none transition-all duration-300 hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none ${startButtonStateClass} lg:flex-none lg:rounded-full`}
            >
              {!isScrolling && (
                <>
                  {/* ring pulse */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute -inset-[3px] block animate-[free-trial-ring_2.8s_ease-in-out_infinite] rounded-[16px] border-2 border-[#A98EFF]/50 lg:rounded-full"
                  />

                  {/* shine */}
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 block overflow-hidden rounded-[16px] lg:rounded-full"
                  >
                    <span className="absolute -inset-y-4 -left-16 w-10 rotate-[18deg] animate-[free-trial-shine_2.6s_ease-in-out_infinite] bg-white/50 blur-md" />
                  </span>

                  <PlayCircleIcon className="relative z-10 hidden h-6 w-6 lg:block" />
                </>
              )}

              <div className="relative z-10 flex flex-col items-center gap-1 leading-none">
                <span
                  className={`whitespace-nowrap font-bold transition-all duration-300 ${titleTextClass}`}
                >
                  {isScrolling ? '무료체험' : '무료체험 시작하기'}
                </span>

                {!isScrolling && (
                  <span className="max-h-5 overflow-hidden whitespace-nowrap text-[12px] font-medium text-white/90 opacity-100 transition-all duration-300 lg:hidden">
                    1레슨 · AI 피드백까지
                  </span>
                )}
              </div>

              {/* FREE */}
              <span
                className={`relative z-10 overflow-hidden whitespace-nowrap rounded-full bg-[#FFD928] font-black leading-none text-[#4B3500] transition-all duration-300 ${freeBadgeStateClass}`}
              >
                FREE
              </span>
            </Button>
          </div>
        </div>
      </FloatingBarFrame>

      <GuestFreeTrialModal
        open={isGuestModalOpen}
        onClose={() => setIsGuestModalOpen(false)}
      />

      <FreeTrialUnavailableModal
        open={isTrialUnavailableOpen}
        onClose={() => setIsTrialUnavailableOpen(false)}
      />
    </>
  );
};

export default DefaultFreeTrialFloatingBar;
